import { useCallback, useRef, useState } from 'react';
import { useSalvaCampo } from './salvaCampo';
import { messaggioDa } from './notifiche';
import { anamnesiVociAPI } from '../services/api';
import { useUscitaProtetta } from './uscitaProtetta';

/**
 * La MODIFICA di una voce di anamnesi (o di una terapia) che si salva da
 * sola, campo per campo, quando se ne esce (regola di Alex del 24 settembre
 * 2026; schermate decise da Alex lo stesso giorno). Niente «Salva»: si
 * chiude con «Chiudi». La voce NUOVA resta un gesto («Aggiungi voce»,
 * «Aggiungi terapia»), perche' crea una riga firmata.
 *
 * Una copia sola per l'anamnesi e per le terapie, sopra il meccanismo
 * dell'anamnesi (utils/salvaCampo.js). Un campo che non e' cambiato non si
 * manda; il server fa lo stesso controllo per conto suo.
 *
 * @param onSalvato  dopo ogni scrittura riuscita (es. avvisare il riepilogo)
 */
export function useModificaVoce(onSalvato) {
    const voceId = useRef(null);
    const salvati = useRef({});
    const [errori, setErrori] = useState({});

    const scrivi = useCallback(async (campo, valore) => {
        try {
            await anamnesiVociAPI.aggiorna(voceId.current, { [campo]: valore });
            salvati.current[campo] = String(valore ?? '');
            setErrori((p) => ({ ...p, [campo]: null }));
        } catch (e) {
            setErrori((p) => ({ ...p, [campo]: messaggioDa(e, 'il salvataggio non è riuscito') }));
            throw e;
        }
    }, []);
    const { stato, salva, nonSalvati } = useSalvaCampo(scrivi, onSalvato);
    /* Un campo rosso e' testo che non e' in cartella: uscendo, si chiede. */
    useUscitaProtetta(nonSalvati.length > 0, nonSalvati.length || null);

    /** Quando si apre la modifica: i valori di partenza sono quelli «salvati». */
    const inizia = useCallback((id, valori) => {
        voceId.current = id;
        salvati.current = Object.fromEntries(Object.entries(valori).map(([k, v]) => [k, String(v ?? '')]));
        setErrori({});
    }, []);

    const esci = (campo, valore) => {
        if (String(valore ?? '') === (salvati.current[campo] ?? '')) return;   // non e' cambiato
        salva(campo, valore);
    };

    const inCorso = Object.values(stato).includes('in_corso');
    const appenaSalvato = Object.values(stato).includes('salvato');
    const erroriAperti = nonSalvati.map((c) => ({ campo: c, messaggio: errori[c] || 'il salvataggio non è riuscito' }));

    return { inizia, esci, riprova: salva, inCorso, appenaSalvato, erroriAperti, nonSalvati };
}
